import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Lock, Eye, EyeOff, ArrowLeft, ShieldCheck, AlertCircle, CheckCircle2, Trash2, AlertTriangle, Users, Heart, ChevronRight, Sparkles, Monitor, Sun, Moon } from 'lucide-react';
import { toast } from 'sonner';
import apiClient from "../api/client";
import PrivateGalleryManage from '../components/settings/PrivateGalleryManage';
import LanguageSelector from '../components/LanguageSelector';
import { useTheme } from '../context/ThemeContext';


export default function SettingsPage() {
  const { logout, token } = useContext(AuthContext);
  const navigate = useNavigate();
  const { preference, theme, setTheme } = useTheme();
  
  // Couple Profile State
  const [coupleInfo, setCoupleInfo] = useState(null);
  const [loadingCouple, setLoadingCouple] = useState(true);
  
  // Her Space access state
  const [herSpaceAllowed, setHerSpaceAllowed] = useState(false);
  
  // Change Password State
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  // Delete Account State
  const [showDeleteSection, setShowDeleteSection] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteAcknowledge, setDeleteAcknowledge] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  
  // Fetch couple info
  useEffect(() => {
    const fetchCoupleInfo = async () => {
      if (!token) return;
      try {
        const res = await apiClient.get(`/couples/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCoupleInfo(res.data.couple);
      } catch (err) {
        console.error("Failed to fetch couple info:", err);
      } finally {
        setLoadingCouple(false);
      }
    };
    fetchCoupleInfo();
  }, [token]);
  
  // Check Her Space access
  useEffect(() => {
    const checkHerSpace = async () => {
      if (!token) return;
      try {
        const res = await apiClient.get(`/her-space/status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setHerSpaceAllowed(res.data.allowed);
      } catch (err) {
        setHerSpaceAllowed(false);
      }
    };
    checkHerSpace();
  }, [token]);

  // Password strength validation
  const validatePassword = (password) => {
    const issues = [];
    if (password.length < 8) issues.push('At least 8 characters');
    if (!/[a-zA-Z]/.test(password)) issues.push('At least one letter');
    if (!/[0-9]/.test(password)) issues.push('At least one number');
    return issues;
  };

  const passwordIssues = validatePassword(newPassword);
  const isPasswordValid = newPassword.length > 0 && passwordIssues.length === 0;
  const passwordsMatch = newPassword === confirmPassword && confirmPassword.length > 0;
  
  const canSubmit = 
    currentPassword.length > 0 && 
    isPasswordValid && 
    passwordsMatch && 
    !isSubmitting;

  // Delete account validation
  const canDelete = 
    deleteAcknowledge &&
    deleteConfirmText.trim().toUpperCase() === 'DELETE' &&
    deletePassword.length >= 6 &&
    !isDeleting;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    
    if (!canSubmit) return;
    
    setIsSubmitting(true);
    
    try {
      const token = localStorage.getItem('token');
      await apiClient.post(
        `/auth/change-password`,
        {
          current_password: currentPassword,
          new_password: newPassword,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      
      // Success! Show message and logout
      toast.success('Password updated. Please sign in again.', {
        duration: 4000,
      });
      
      // Clear form
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      
      // Logout and redirect
      setTimeout(() => {
        logout();
        navigate('/auth', { 
          state: { message: 'Password updated successfully. Please sign in with your new password.' }
        });
      }, 1500);
      
    } catch (error) {
      const detail = error.response?.data?.detail;
      
      if (detail === 'Current password is incorrect') {
        setErrors({ currentPassword: 'Incorrect password' });
      } else if (detail?.includes('at least')) {
        setErrors({ newPassword: detail });
      } else if (error.response?.status === 429) {
        setErrors({ general: 'Too many attempts. Please try again later.' });
      } else {
        setErrors({ general: detail || 'Failed to change password. Please try again.' });
        toast.error(detail || 'Failed to change password');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!canDelete) return;
    
    setDeleteError('');
    setIsDeleting(true);
    
    try {
      await apiClient.delete(`/auth/account`, {
        headers: { Authorization: `Bearer ${token}` },
        data: { 
          password: deletePassword, 
          confirm: deleteConfirmText 
        },
      });
      
      toast.success('Your account has been deleted. We\'re sorry to see you go.', {
        duration: 5000,
      });
      
      // Log out and redirect to home
      setTimeout(() => {
        logout();
        navigate('/', { replace: true });
      }, 1500);
      
    } catch (error) {
      const detail = error.response?.data?.detail;
      setDeleteError(detail || 'Unable to delete account. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const resetDeleteForm = () => {
    setShowDeleteSection(false);
    setDeletePassword('');
    setDeleteConfirmText('');
    setDeleteAcknowledge(false);
    setDeleteError('');
  };

  return (
    <div className="min-h-screen surface-page text-slate-900 dark:text-white">
      <div className="mx-auto max-w-2xl px-4 py-6 md:py-10">
        {/* Header */}
        <header className="mb-8">
          <button
            onClick={() => navigate(-1)}
            className="mb-4 inline-flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
          <h1 className="text-2xl font-bold tracking-tight">Settings</h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            Manage your account security and preferences
          </p>
        </header>

        {/* Couple Profile Section */}
        <section className="mb-6 rounded-xl border border-pink-200 dark:border-pink-500/30 bg-gradient-to-br from-pink-50 to-purple-50 dark:from-pink-900/20 dark:to-purple-900/20 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-pink-100 dark:bg-pink-500/20">
                <Users className="w-5 h-5 text-pink-600 dark:text-pink-400" />
              </div>
              <div>
                <h2 className="text-lg font-semibold">Couple Profile</h2>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {coupleInfo?.status === "active" 
                    ? `Linked with ${coupleInfo?.partner_b?.display_name || "your partner"}`
                    : coupleInfo?.status === "pending"
                    ? "Waiting for partner to accept"
                    : "Link two accounts into one shared profile"
                  }
                </p>
              </div>
            </div>
            <button
              onClick={() => navigate('/couple')}
              className="flex items-center gap-1 px-4 py-2 rounded-lg bg-pink-100 hover:bg-pink-200 dark:bg-pink-600/20 dark:hover:bg-pink-600/30 text-pink-700 dark:text-pink-300 transition"
              data-testid="couple-profile-btn"
            >
              {coupleInfo ? "Manage" : "Create"}
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </section>

        {/* Her Space Section - Only visible to women */}
        {herSpaceAllowed && (
          <section className="mb-6 rounded-xl border border-rose-200 dark:border-rose-500/20 bg-gradient-to-br from-rose-50 to-purple-50 dark:from-rose-900/10 dark:to-purple-900/10 p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-rose-100 dark:bg-rose-500/20">
                  <Sparkles className="w-5 h-5 text-rose-600 dark:text-rose-400" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold">Her Space</h2>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    A women-first space for grounding, learning, and real talk.
                  </p>
                </div>
              </div>
              <button
                onClick={() => navigate('/her-space')}
                className="flex items-center gap-1 px-4 py-2 rounded-lg bg-rose-100 hover:bg-rose-200 dark:bg-rose-600/20 dark:hover:bg-rose-600/30 text-rose-700 dark:text-rose-300 transition"
                data-testid="her-space-btn"
              >
                Enter
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </section>
        )}

        {/* Appearance Section */}
        <section className="mb-6 rounded-xl p-6 surface-card">
          <div className="flex items-center gap-3 mb-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 dark:bg-slate-700/50">
              <Monitor className="w-5 h-5 text-slate-500 dark:text-slate-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold">Appearance</h2>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Choose how Unity Love looks for you
              </p>
            </div>
          </div>
          
          <div className="grid grid-cols-3 gap-2">
            <button
              onClick={() => setTheme('system')}
              className={`flex flex-col items-center gap-2 p-4 rounded-xl border transition ${
                preference === 'system'
                  ? 'border-rose-500 bg-rose-500/10 text-rose-600 dark:text-white'
                  : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 dark:border-slate-700 dark:bg-slate-800/50 dark:text-slate-400 dark:hover:border-slate-600'
              }`}
              data-testid="theme-system-btn"
            >
              <Monitor className="w-5 h-5" />
              <span className="text-sm font-medium">System</span>
              {preference === 'system' && (
                <span className="text-xs text-slate-500">
                  (currently {theme === 'dark' ? 'Dark' : 'Light'})
                </span>
              )}
            </button>
            <button
              onClick={() => setTheme('light')}
              className={`flex flex-col items-center gap-2 p-4 rounded-xl border transition ${
                preference === 'light'
                  ? 'border-rose-500 bg-rose-500/10 text-rose-600 dark:text-white'
                  : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 dark:border-slate-700 dark:bg-slate-800/50 dark:text-slate-400 dark:hover:border-slate-600'
              }`}
              data-testid="theme-light-btn"
            >
              <Sun className="w-5 h-5" />
              <span className="text-sm font-medium">Light</span>
            </button>
            <button
              onClick={() => setTheme('dark')}
              className={`flex flex-col items-center gap-2 p-4 rounded-xl border transition ${
                preference === 'dark'
                  ? 'border-rose-500 bg-rose-500/10 text-rose-600 dark:text-white'
                  : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 dark:border-slate-700 dark:bg-slate-800/50 dark:text-slate-400 dark:hover:border-slate-600'
              }`}
              data-testid="theme-dark-btn"
            >
              <Moon className="w-5 h-5" />
              <span className="text-sm font-medium">Dark</span>
            </button>
          </div>
        </section>

        {/* Change Password Section */}
        <section className="rounded-xl p-6 surface-card">
          <div className="flex items-center gap-3 mb-6">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-rose-100 dark:bg-rose-500/20">
              <Lock className="w-5 h-5 text-rose-500 dark:text-rose-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold">Change Password</h2>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Update your password to keep your account secure
              </p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* General Error */}
            {errors.general && (
              <div className="rounded-lg bg-red-500/10 border border-red-500/30 p-3 flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-400 dark:text-red-300">{errors.general}</p>
              </div>
            )}

            {/* Current Password */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Current Password
              </label>
              <div className="relative">
                <input
                  type={showCurrentPassword ? 'text' : 'password'}
                  value={currentPassword}
                  onChange={(e) => {
                    setCurrentPassword(e.target.value);
                    setErrors((prev) => ({ ...prev, currentPassword: null }));
                  }}
                  placeholder="Enter your current password"
                  className={`w-full rounded-lg border px-4 py-3 pr-12 focus:outline-none focus:ring-2 transition surface-input ${
                    errors.currentPassword 
                      ? 'border-red-500/50 focus:ring-red-500/30' 
                      : 'focus:ring-rose-500/30 focus:border-rose-500/50'
                  }`}
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition"
                >
                  {showCurrentPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {errors.currentPassword && (
                <p className="mt-1.5 text-sm text-red-400 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.currentPassword}
                </p>
              )}
            </div>

            {/* New Password */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                New Password
              </label>
              <div className="relative">
                <input
                  type={showNewPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => {
                    setNewPassword(e.target.value);
                    setErrors((prev) => ({ ...prev, newPassword: null }));
                  }}
                  placeholder="Enter your new password"
                  className={`w-full rounded-lg border px-4 py-3 pr-12 focus:outline-none focus:ring-2 transition surface-input ${
                    errors.newPassword 
                      ? 'border-red-500/50 focus:ring-red-500/30' 
                      : 'focus:ring-rose-500/30 focus:border-rose-500/50'
                  }`}
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition"
                >
                  {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {errors.newPassword && (
                <p className="mt-1.5 text-sm text-red-400 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.newPassword}
                </p>
              )}
              
              {/* Password Strength Indicators */}
              {newPassword.length > 0 && (
                <div className="mt-2 space-y-1">
                  {['At least 8 characters', 'At least one letter', 'At least one number'].map((rule) => {
                    const passed = !passwordIssues.includes(rule);
                    return (
                      <div key={rule} className="flex items-center gap-2 text-xs">
                        {passed ? (
                          <CheckCircle2 className="w-4 h-4 text-emerald-500 dark:text-emerald-400" />
                        ) : (
                          <div className="w-4 h-4 rounded-full border border-slate-300 dark:border-slate-600" />
                        )}
                        <span className={passed ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-500'}>
                          {rule}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Confirm New Password */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Confirm New Password
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your new password"
                  className={`w-full rounded-lg border px-4 py-3 pr-12 focus:outline-none focus:ring-2 transition surface-input ${
                    confirmPassword.length > 0 && !passwordsMatch
                      ? 'border-red-500/50 focus:ring-red-500/30'
                      : 'focus:ring-rose-500/30 focus:border-rose-500/50'
                  }`}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition"
                >
                  {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {confirmPassword.length > 0 && !passwordsMatch && (
                <p className="mt-1.5 text-sm text-red-400 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  Passwords do not match
                </p>
              )}
              {confirmPassword.length > 0 && passwordsMatch && (
                <p className="mt-1.5 text-sm text-emerald-400 flex items-center gap-1">
                  <CheckCircle2 className="w-4 h-4" />
                  Passwords match
                </p>
              )}
            </div>

            {/* Warning Notice */}
            <div className="rounded-lg bg-amber-50 dark:bg-amber-500/10 border border-amber-300 dark:border-amber-500/30 p-3 flex items-start gap-2">
              <ShieldCheck className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-800 dark:text-amber-200">
                Changing your password will sign you out of all devices. You&apos;ll need to sign in again with your new password.
              </p>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={!canSubmit}
              className={`w-full rounded-lg py-3 px-4 font-semibold text-white transition ${
                canSubmit
                  ? 'bg-rose-600 hover:bg-rose-500 cursor-pointer'
                  : 'bg-slate-700 cursor-not-allowed opacity-60'
              }`}
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Updating Password...
                </span>
              ) : (
                'Update Password'
              )}
            </button>
          </form>
        </section>

        {/* Language Settings */}
        <LanguageSelector />

        {/* Private Gallery Management */}
        <PrivateGalleryManage />

        {/* Security Section: Log out all devices */}
        <section className="mt-8 rounded-xl p-6 surface-card">
          <div className="flex items-center gap-3 mb-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 dark:bg-slate-700">
              <ShieldCheck className="w-5 h-5 text-slate-600 dark:text-slate-300" />
            </div>
            <div>
              <h2 className="text-lg font-semibold">Security</h2>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Manage your account security
              </p>
            </div>
          </div>

          <div className="space-y-4">
            {/* Log out all devices button */}
            <div className="flex items-center justify-between p-4 rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700">
              <div>
                <h3 className="font-medium">Log out of all devices</h3>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  This will sign you out everywhere, including this device. Use if you suspect your account has been compromised.
                </p>
              </div>
              <button
                onClick={async () => {
                  if (!window.confirm('This will log you out of all devices including this one. Continue?')) return;
                  try {
                    const token = localStorage.getItem('token');
                    await apiClient.post(`/auth/logout-all-devices`, {}, {
                      headers: { Authorization: `Bearer ${token}` }
                    });
                    toast.success('Logged out of all devices. Please sign in again.');
                    localStorage.removeItem('token');
                    localStorage.removeItem('user');
                    window.location.href = '/auth';
                  } catch (err) {
                    toast.error('Failed to log out devices. Try again.');
                  }
                }}
                className="px-4 py-2 rounded-lg bg-slate-200 dark:bg-slate-700 text-slate-900 dark:text-white font-medium hover:bg-slate-300 dark:hover:bg-slate-600 transition whitespace-nowrap"
                data-testid="logout-all-devices-btn"
              >
                Log out all
              </button>
            </div>
          </div>
        </section>

        {/* Delete Account Section */}
        <section className="mt-8 rounded-xl border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-950/20 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-100 dark:bg-red-500/20">
              <Trash2 className="w-5 h-5 text-red-600 dark:text-red-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-red-800 dark:text-red-200">Delete Account</h2>
              <p className="text-sm text-red-600 dark:text-red-300/70">
                Permanently remove your account and data
              </p>
            </div>
          </div>

          <p className="text-sm text-red-700 dark:text-red-200/80 mb-4">
            This permanently removes your profile, matches, messages, and photos. 
            Some records may be retained for safety, fraud prevention, and payment auditing, 
            but your personal profile data is removed or anonymized.
          </p>

          {!showDeleteSection ? (
            <button
              onClick={() => setShowDeleteSection(true)}
              className="px-4 py-2.5 rounded-lg bg-red-600/80 text-white font-medium hover:bg-red-600 transition"
            >
              Start account deletion
            </button>
          ) : (
            <div className="space-y-4 border-t border-red-200 dark:border-red-800/40 pt-4 mt-4">
              <div className="rounded-lg bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-700/40 p-3 flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-800 dark:text-red-200">
                  <strong>This action cannot be undone.</strong> Your profile will be immediately 
                  removed from discovery and you will lose access to your account.
                </p>
              </div>

              {/* Confirm text */}
              <div>
                <label className="block text-sm font-medium text-red-800 dark:text-red-200 mb-2">
                  Type <span className="font-bold">DELETE</span> to confirm
                </label>
                <input
                  type="text"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder='Type "DELETE"'
                  className="w-full p-3 rounded-lg bg-red-50 dark:bg-black/30 border border-red-300 dark:border-red-800/50 text-slate-900 dark:text-white placeholder-red-400/70 dark:placeholder-red-400/50 focus:outline-none focus:ring-2 focus:ring-red-500/40"
                />
              </div>

              {/* Password */}
              <div>
                <label className="block text-sm font-medium text-red-800 dark:text-red-200 mb-2">
                  Enter your password
                </label>
                <input
                  type="password"
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                  placeholder="Your current password"
                  className="w-full p-3 rounded-lg bg-red-50 dark:bg-black/30 border border-red-300 dark:border-red-800/50 text-slate-900 dark:text-white placeholder-red-400/70 dark:placeholder-red-400/50 focus:outline-none focus:ring-2 focus:ring-red-500/40"
                />
              </div>

              {/* Acknowledgment checkbox */}
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={deleteAcknowledge}
                  onChange={(e) => setDeleteAcknowledge(e.target.checked)}
                  className="mt-1 w-4 h-4 rounded border-red-600 bg-red-900/30 text-red-500 focus:ring-red-500/40"
                />
                <span className="text-sm text-red-700 dark:text-red-200/90">
                  I understand this action is permanent and I will lose access to my account, 
                  matches, messages, and all associated data.
                </span>
              </label>

              {/* Error message */}
              {deleteError && (
                <div className="rounded-lg bg-red-500/20 border border-red-500/40 p-3 flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-300">{deleteError}</p>
                </div>
              )}

              {/* Action buttons */}
              <div className="flex gap-3 pt-2">
                <button
                  onClick={resetDeleteForm}
                  disabled={isDeleting}
                  className="px-4 py-2.5 rounded-lg bg-slate-700 text-white font-medium hover:bg-slate-600 transition disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteAccount}
                  disabled={!canDelete}
                  className={`px-4 py-2.5 rounded-lg font-medium text-white transition ${
                    canDelete
                      ? 'bg-red-600 hover:bg-red-500 cursor-pointer'
                      : 'bg-red-900/50 cursor-not-allowed opacity-60'
                  }`}
                >
                  {isDeleting ? (
                    <span className="flex items-center gap-2">
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Deleting...
                    </span>
                  ) : (
                    'Delete my account'
                  )}
                </button>
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
